import React, { useEffect, useState } from 'react';
import { http, general, formValidator, AlertService, FxInput, FxSelect, FxMobile, FxTextarea } from '../../fx-core';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import './Loyalty.scss';

const Loyalty = () => {

  /* ── loyalty state ── */
  const [loyaltyPoints,   setLoyaltyPoints]   = useState(0);
  const [loyaltyProducts, setLoyaltyProducts] = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [savedAddressId,  setSavedAddressId]  = useState(0);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [claiming,        setClaiming]        = useState(false);

  /* ── address form state ── */
  const defaultAddress = {
    fullName:      '',
    displayName:   '',
    mobile:        '',
    fullAddress:   '',
    country:       'India',
    city:          'Coimbatore',
    pinCode:       '',
    addressTypeId: -1,
    locationId:    -1,
    isDefault:     false,
    id:            0,
    customerId:    0,
    countryCode:   '',
  };
  const [addrItem,     setAddrItem]     = useState(defaultAddress);
  const [addrErrors,   setAddrErrors]   = useState<any>({});
  const [locations,    setLocations]    = useState<any[]>([]);
  const [countryCode,  setCountryCode]  = useState<any[]>([]);
  const [savingAddr,   setSavingAddr]   = useState(false);

  const sid = () => localStorage.getItem('sessionId');

  /* ─────────────────────────────────────
     ADDRESS FORM HELPERS  (same as AddressForm)
  ───────────────────────────────────── */
  const handleAddrInputChange = (item) => {
    handleAddrItemChange({ [item.name]: item.value });
    item.validations = getAddrInputValidations(item.name);
    const validationResult = formValidator.validate(item);
    updateAddrErrors({ [item.name]: validationResult });
  };

  const handleAddrCheckbox = (event) => {
    handleAddrItemChange({ [event.target.name]: event.target.checked });
  };

  const handleAddrItemChange = (data) => {
    setAddrItem(prev => ({ ...prev, ...data }));
  };

  const validationMap = {
    fullName:    ['required'],
    mobile:      ['required', 'mobileno'],
    fullAddress: ['required'],
    pinCode:     ['required', 'pincode'],
  };

  const getAddrInputValidations = (name) => validationMap[name];

  const bulkValidateAddr = () => {
    const items: any[] = Object.keys(validationMap).map(key => ({
      name: key, value: addrItem[key], validations: validationMap[key],
    }));
    const result = formValidator.bulkValidate(items);
    updateAddrErrors(result.errors);
    return result.isValid;
  };

  const updateAddrErrors = (data) => {
    setAddrErrors(prev => ({ ...prev, ...data }));
  };

  const hasAddrError = (field, method) => addrErrors?.[field]?.[method];

  const initAddressLookup = () => {
    const inputData = {
      lookups: {
        Tier4Locations: { default: true },
        CountryCode:    { default: false },
      },
      filters: {},
    };
    http.post({ url: 'options/lookup', data: inputData }).then(response => {
      setLocations(response.data.Tier4Locations || []);
      setCountryCode(response.data.CountryCode  || []);
    });
  };

  const saveAddress = () => {
    const isValid = bulkValidateAddr();
    if (!isValid) return;

    if (addrItem.locationId === -1) {
      AlertService.showErrorMsg('Please select location');
      return;
    }

    const payload = { ...addrItem };
    if (payload.mobile) {
      payload.countryCode = payload.countryCode || '+91';
      payload.mobile      = payload.countryCode + payload.mobile;
    }

    setSavingAddr(true);
    http.post({ url: 'address', data: payload }).then(res => {
      const addressId = res.data;   // ← id returned from POST address
      setSavedAddressId(addressId);
      setShowAddressForm(false);
      setAddrItem(defaultAddress);
      setAddrErrors({});
      AlertService.showSuccessMsg();
    }).catch(() => {
      // stay on form so user can retry
    }).finally(() => {
      setSavingAddr(false);
    });
  };

  /* ─────────────────────────────────────
     LOYALTY API
  ───────────────────────────────────── */
  const getLoyaltyPoints = () => {
    http.get({ url: 'loyalty/points/summary', data: sid() }).then(response => {
      setLoyaltyPoints(response.data.points || 0);
    });
  };

  const getLoyaltyProducts = () => {
    http.get({ url: 'loyalty/products/list', data: sid() }).then(response => {
      setLoyaltyProducts(response.data || []);
    }).catch(() => {
      setLoyaltyProducts([]);
    }).finally(() => {
      setLoading(false);
    });
  };

  const claimOrder = () => {
    if (!selectedProduct || !savedAddressId) return;
    setClaiming(true);
    http.post({
      url: 'loyalty/claim/order',
      data: {
        sessionId:        sid(),
        loyaltyProductId: selectedProduct.id,
        addressId:        savedAddressId,
        orderType:1
      },
    }).then(() => {
      setLoyaltyPoints(p => p - selectedProduct.loyaltyPoints);
      setSelectedProduct(null);
      setSavedAddressId(0);
    }).catch(() => {
      // keep panel open so user can retry
    }).finally(() => {
      setClaiming(false);
    });
  };

  const handleSelectProduct = (product) => {
    if (selectedProduct?.id === product.id) {
      setSelectedProduct(null);
      setSavedAddressId(0);
      setShowAddressForm(false);
      return;
    }
    setSelectedProduct(product);
    setSavedAddressId(0);
    setShowAddressForm(false);
  };

  const handleShowAddressForm = () => {
    initAddressLookup();
    setShowAddressForm(true);
  };

  const handleChangeAddress = () => {
    setSavedAddressId(0);
    handleShowAddressForm();
  };

  useEffect(() => {
    getLoyaltyPoints();
    getLoyaltyProducts();
  }, []);

  const canAfford = (pts) => loyaltyPoints >= pts;

  /* ─────────────────────────────────────
     RENDER
  ───────────────────────────────────── */
  return (
    <div className="loyalty-container">

      {/* ── Points Card ── */}
      <div className="points-card">
        <div className="points-label">Available Points</div>
        <div className="points-value">{loyaltyPoints}</div>
        <div className="points-unit">loyalty points</div>
      </div>

      {/* ── Products ── */}
      <div className="section-title">Redeem Products</div>

      {loading ? (
        <div className="products-grid">
          {[1, 2, 3, 4].map(i => <div key={i} className="skeleton-card" />)}
        </div>
      ) : loyaltyProducts.length === 0 ? (
        <div className="empty-state">
          <span>🎁</span> No products available right now.
        </div>
      ) : (
        <div className="products-grid">
          {loyaltyProducts.map((product: any) => {
            const isSelected = selectedProduct?.id === product.id;
            const affordable = canAfford(product.loyaltyPoints);
            return (
              <div
                key={product.id}
                className={['product-card', isSelected ? 'selected' : '', !affordable ? 'disabled' : ''].filter(Boolean).join(' ')}
                onClick={() => affordable && handleSelectProduct(product)}
              >
                {isSelected && <div className="product-tick">✓</div>}
                <div className="product-img">
                  <img
                    src={general.getImageUrl(product.imagePath)}
                    alt={product.itemName}
                    onError={(e: any) => { e.target.style.display = 'none'; }}
                  />
                </div>
                <div className="product-body">
                  <div className="product-name" title={product.itemName}>{product.itemName}</div>
                  <div className="product-pts">⭐ {product.loyaltyPoints} pts</div>
                  {product.price > 0 && <div className="product-price">₹{product.price}</div>}
                  <div className="product-stock">Stock: {product.stock}</div>
                  {!affordable && (
                    <div className="product-need-more">
                      Need {product.loyaltyPoints - loyaltyPoints} more pts
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Claim Panel ── */}
      {selectedProduct && (
        <div className="claim-panel">
          <div className="section-title">Complete Your Claim</div>

          {/* Selected product summary */}
          <div className="selected-product-row">
            <div className="sel-thumb">
              <img
                src={general.getImageUrl(selectedProduct.imagePath)}
                alt={selectedProduct.itemName}
                onError={(e: any) => { e.target.style.display = 'none'; }}
              />
            </div>
            <div className="sel-info">
              <div className="sel-name">{selectedProduct.itemName}</div>
              <div className="sel-pts">
                &minus;{selectedProduct.loyaltyPoints} pts &rarr;&nbsp;
                {loyaltyPoints - selectedProduct.loyaltyPoints} remaining
              </div>
            </div>
          </div>

          {/* ── Delivery Address ── */}
          <div className="section-title">Delivery Address</div>

          {savedAddressId > 0 ? (
            /* address already saved */
            <div className="address-saved-row">
              <div className="address-saved-banner">✓ Address added successfully</div>
              <button className="change-addr-btn" onClick={handleChangeAddress}>Change</button>
            </div>

          ) : showAddressForm ? (
            /* inline address form — same fields as AddressForm component */
            <div className="address-form-wrapper">

              <div className="m-1">
                <FxInput name="fullName" variant="outlined" label="Full name" fullWidth
                  value={addrItem.fullName} onValueChange={handleAddrInputChange} />
                {hasAddrError('fullName', 'required') && <div className="error">Field is required</div>}
              </div>

              <div className="m-1 pt-3">
                <FxMobile value={addrItem.mobile} onValueChange={handleAddrInputChange} countryCode={countryCode} />
                {hasAddrError('mobile', 'required') && <div className="error">Field is required</div>}
                {hasAddrError('mobile', 'mobileno') && <div className="error">Please enter valid mobile number</div>}
              </div>

              <div className="m-1 pt-3">
                <FxTextarea name="fullAddress" variant="outlined" fullWidth size="small" label="Full Address"
                  value={addrItem.fullAddress} onValueChange={handleAddrInputChange} maxRows={4} />
                {hasAddrError('fullAddress', 'required') && <div className="error">Field is required</div>}
              </div>

              <div className="m-1 pt-3">
                <FxInput name="pinCode" type="number" variant="outlined" label="Pincode" fullWidth
                  value={addrItem.pinCode} onValueChange={handleAddrInputChange} />
                {hasAddrError('pinCode', 'required') && <div className="error">Field is required</div>}
                {hasAddrError('pinCode', 'pincode') && <div className="error">Invalid Pincode</div>}
              </div>

              <div className="m-1 pt-3">
                <FxSelect name="locationId" variant="outlined" label="Location"
                  options={locations}
                  selectedValue={addrItem.locationId}
                  valueField="id"
                  displayField="text"
                  size="small"
                  fullWidth
                  onValueChange={handleAddrInputChange} />
              </div>

              <div className="font-12">
                <Checkbox name="isDefault" color="primary"
                  checked={addrItem.isDefault}
                  value={addrItem.isDefault}
                  onChange={handleAddrCheckbox} />
                Set as Default
              </div>

              <div className="m-1 pt-2 text-center">
                <Button variant="contained" size="medium" color="primary"
                  onClick={saveAddress} disabled={savingAddr}>
                  {savingAddr ? 'Saving...' : 'Save'}
                </Button>
              </div>

            </div>

          ) : (
            /* no address yet */
            <button className="add-address-btn" onClick={handleShowAddressForm}>
              ＋ Add Delivery Address
            </button>
          )}

          {/* Claim button */}
          <button
            className="claim-btn"
            onClick={claimOrder}
            disabled={claiming || !savedAddressId}
          >
            {claiming
              ? 'Processing...'
              : !savedAddressId
                ? 'Add Delivery Address to Claim'
                : `Claim for ${selectedProduct.loyaltyPoints} Points`}
          </button>

        </div>
      )}

    </div>
  );
};

export default Loyalty;